// Pure localStorage helpers for the warm-resume auth cache. Kept free of any
// Angular / Sentry imports so they can be unit-tested standalone. AuthService
// delegates its static warm-resume methods here.

// structural match for `UserAuth` in auth.service.ts (kept local to avoid a
// circular import through auth.service; the shape is stable and small)
export interface WarmResumeUserAuth {
  uid: string;
  name: string;
  email: string;
  avatarUrl: string;
  method: string;
}

const UID_KEY = 'warm_resume_uid';
const AUTH_KEY = 'warm_resume_user_auth';

export function getWarmResumeUid(): string | null {
  return localStorage.getItem(UID_KEY);
}

// returns the cached UserAuth only when every required field is a string and
// uid matches the primary flag; half-written or tampered cache yields null.
// Rebuilds the returned object from validated fields so any extra keys present
// in the raw JSON are dropped.
export function getWarmResumeUserAuth(): WarmResumeUserAuth | null {
  const raw = localStorage.getItem(AUTH_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<WarmResumeUserAuth>;
    if (typeof parsed?.uid !== 'string' || parsed.uid !== localStorage.getItem(UID_KEY)) return null;
    if (typeof parsed.email !== 'string' || typeof parsed.name !== 'string'
        || typeof parsed.avatarUrl !== 'string' || typeof parsed.method !== 'string') return null;
    return {
      uid: parsed.uid,
      name: parsed.name,
      email: parsed.email,
      avatarUrl: parsed.avatarUrl,
      method: parsed.method,
    };
  } catch {
    // a damaged cache is treated like a missing one: the app signs in the normal, slower way
    return null;
  }
}

// persists both the UID (primary flag) and the full UserAuth so the next boot
// can synthesize login from localStorage without awaiting Firebase authStateReady
export function setWarmResumeFlag(user: WarmResumeUserAuth) {
  localStorage.setItem(UID_KEY, user.uid);
  localStorage.setItem(AUTH_KEY, JSON.stringify(user));
}

// must this boot give up a stored warm resume because the shared catalog (global_common) has
// never finished downloading on this device (df-e30dd9684cf6)? the cold boot sets the flag before
// that download is done, and a warm resume reads the catalog from the device before any server
// login, so a reload during the first download would read every missing exercise as "does not
// exist". the cold start logs in first and reads through to the server instead. when the browser
// says there is no network the server cannot answer either way, so the warm resume stays
export function warmResumeMustWaitForCatalog(input: {
  commonDbHasSyncedOnThisDevice: boolean;
  browserSaysDefinitelyOffline: boolean;
}): boolean {
  return !input.commonDbHasSyncedOnThisDevice && !input.browserSaysDefinitelyOffline;
}

// both keys are invariant-paired — always cleared together so a lingering
// `warm_resume_user_auth` without a matching `warm_resume_uid` can never
// trigger the fast path
export function clearWarmResumeFlag() {
  localStorage.removeItem(UID_KEY);
  localStorage.removeItem(AUTH_KEY);
}
